import { Card, EmptyState, StatusBadge } from '@/components/ui';
import { Page } from '@/components/app/Page';
import type { AppNavItem } from '@/app/navigation';
import { useAuth } from '@/lib/auth/AuthProvider';
import styles from '@/app/app.module.css';

/**
 * Screen for modules whose foundation (permissions, navigation, route guard) exists but whose
 * domain screens ship in a later phase. It shows real information only: what the module covers
 * and which of the user's permissions grant access. No placeholder data is fabricated.
 */
export function ModuleFoundationPage({ item, section }: { item: AppNavItem; section?: string }) {
  const { me } = useAuth();
  const Icon = item.icon;
  const required = [...(item.requires?.allOf ?? []), ...(item.requires?.anyOf ?? [])];
  const held = new Set([
    ...(me?.context.permissions.organization ?? []),
    ...Object.values(me?.context.permissions.schools ?? {}).flat(),
  ]);

  return (
    <Page
      title={item.label}
      description={item.description}
      breadcrumbs={[
        { label: 'Dashboard', href: '/dashboard' },
        ...(section ? [{ label: section }] : []),
        { label: item.label },
      ]}
      meta={<StatusBadge tone="info">Module in development</StatusBadge>}
    >
      <div className={styles.twoColumn}>
        <Card>
          <EmptyState
            icon={Icon ? <Icon size={40} color="currentColor" variant="Bulk" /> : undefined}
            title={`${item.label} is being built`}
            description="The security, tenancy and time-zone foundation for this module is in place. Its screens and records will appear here in an upcoming release."
          />
        </Card>
        <Card title="Your access" description="Permissions that grant access to this area.">
          <ul className={styles.chipList}>
            {required.map((p) => (
              <li key={p}>
                <StatusBadge tone={held.has(p) ? 'success' : 'neutral'} dot>
                  {p}
                </StatusBadge>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </Page>
  );
}
